import React, { useState, useEffect, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';

const useStyles = makeStyles({
  root: {
    width: '100%',
    height: 'calc(100% - 60px)',
    display: 'flex',
    flexDirection: 'column'
  },
  container: {
    flex: 1,
    overflow: 'auto'
  },
  table: {
    minWidth: 650,
  },
  tableCell: {
    padding: '8px 16px',
    fontSize: '14px'
  },
  tableHeadCell: {
    padding: '12px 16px',
    fontSize: '14px',
    fontWeight: 600,
    backgroundColor: '#f5f5f5'
  },
  menuPaper: {
    zIndex: 6005
  },
  menuRoot: {
    zIndex: 6005
  }
});

// Calculate rows per page based on window size
const calculateRowsPerPage = (windowHeight, windowWidth) => {
  // Estimate available space for table content
  // Account for popup padding, header, pagination, and close button
  const popupPadding = 60; // 30px top + 30px bottom
  const headerHeight = 50; // Table header height
  const paginationHeight = 60; // TablePagination component height
  const closeButtonHeight = 60; // Close button and spacing
  const backButtonHeight = 50; // Back button height

  // Calculate available height for table rows
  const availableHeight = windowHeight - popupPadding - headerHeight - paginationHeight - closeButtonHeight - backButtonHeight;

  // Estimate row height (including padding and borders)
  const estimatedRowHeight = 48; // Material-UI default row height

  // Calculate how many rows can fit
  const maxRows = Math.floor(availableHeight / estimatedRowHeight);

  // Ensure minimum and maximum bounds
  const minRows = 5;
  const maxRowsLimit = 50;

  let calculatedRows = Math.max(minRows, Math.min(maxRows, maxRowsLimit));

  // For very small screens, use smaller values
  if (windowWidth < 768) {
    calculatedRows = Math.min(calculatedRows, 10);
  }

  return calculatedRows;
};

export default function DocumentPreview(props) {
  const classes = useStyles();
  const [page, setPage] = React.useState(0);
  const [columnsTableJson, setColumnsTableJson] = useState();
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });

  const table_json = props.table_json;

  // Calculate rows per page based on window size
  const calculatedRowsPerPage = calculateRowsPerPage(windowSize.height, windowSize.width);
  const [rowsPerPage, setRowsPerPage] = React.useState(calculatedRowsPerPage);

  // Handle window resize
  const handleResize = useCallback(() => {
    setWindowSize({
      width: window.innerWidth,
      height: window.innerHeight
    });
  }, []);

  // Add and remove resize listener
  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  // Update rows per page when window size changes
  useEffect(() => {
    const newRowsPerPage = calculateRowsPerPage(windowSize.height, windowSize.width);
    setRowsPerPage(newRowsPerPage);
    // Reset to first page when rows per page changes
    setPage(0);
  }, [windowSize]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  function getColumns() {
    if (table_json !== undefined) {
      let col = table_json['file']['columns'];
      setColumnsTableJson(col);
    }
  }

  // Get total number of rows from table data
  const getTotalRows = () => {
    return table_json && table_json.file && table_json.file.data ? table_json.file.data.length : 0;
  };

  // Generate pagination options based on window size
  const getPaginationOptions = () => {
    const totalRows = getTotalRows();
    const options = [calculatedRowsPerPage];

    // Add common options if they make sense for the data size
    if (totalRows > calculatedRowsPerPage * 2) {
      options.push(calculatedRowsPerPage * 2);
    }
    if (totalRows > calculatedRowsPerPage * 4) {
      options.push(calculatedRowsPerPage * 4);
    }
    if (totalRows > 100) {
      options.push(100);
    }

    // Remove duplicates and sort
    return [...new Set(options)].sort((a, b) => a - b);
  };

  return (
    <div className="rules-popup">
      <div className="rules-popup-content">
        <div className="close-popup" onClick={() => props.closePopup()}></div>
        <Paper className={classes.root}>
          <TableContainer className={classes.container}>
            <Table stickyHeader aria-label="sticky table" className={classes.table}>
              <TableHead>
                <TableRow>
                  {table_json !== undefined && table_json['file']['columns'] !== undefined &&
                    table_json['file']['columns'].map((column, i) => {
                      return (
                        <TableCell key={i} className={classes.tableHeadCell}>
                          {column}
                        </TableCell>
                      )
                    })}
                </TableRow>
              </TableHead>
              <TableBody>
                {table_json && table_json['file'] && table_json['file']['data'] &&
                  table_json['file']['data'].slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, id) => {
                    const actualIndex = page * rowsPerPage + id;
                    return (
                      <TableRow hover role="checkbox" tabIndex={-1} key={actualIndex}>
                        {table_json['file']['columns'].map((column, i) => {
                          const value = table_json['file']['data'][actualIndex] ? table_json['file']['data'][actualIndex][i] : null;
                          // Ensure we always render a valid React node
                          const displayValue = (() => {
                            if (value === null || value === undefined) {
                              return '';
                            }
                            if (column.format && typeof value === 'number') {
                              return column.format(value);
                            }
                            return String(value);
                          })();

                          return (
                            <TableCell key={i} className={classes.tableCell}>
                              {displayValue}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={getPaginationOptions()}
            component="div"
            count={getTotalRows()}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Rows per page:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
            SelectProps={{
              MenuProps: {
                classes: { paper: classes.menuPaper, root: classes.menuRoot },
                style: { zIndex: 6005 },
                getContentAnchorEl: null,
                anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
                transformOrigin: { vertical: 'top', horizontal: 'right' },
                disablePortal: false
              }
            }}
          />
        </Paper>
        <button onClick={() => props.handleCsvClick()}>Back</button>
      </div>
    </div>
  );
}
